import { useCallback, useEffect, useMemo, useState } from 'react';
import { FileText, Inbox } from 'lucide-react';
import { toast } from 'sonner';
import { instructorAPI } from '../../lib/api';
import { t } from '../../lib/i18n';
import {
  Avatar, EmptyPanel, FilterPills, PageHeader, SearchBox, adminBtnDanger, adminBtnOutline, adminBtnPrimary,
  adminCard, fmtDate, statusBadge,
} from '../../components/adminUi';

// Admin 6 · Applications (s134 redesign): status pills with counts + search;
// one card per application (avatar, name, contact line, status, date, the
// applicant's message, attached document, Reject / Approve as instructor).
const STATUSES = ['pending', 'approved', 'rejected'];

export default function ApplicationsPage() {
  const [tab, setTab] = useState('pending');
  const [all, setAll] = useState(null);
  const [search, setSearch] = useState('');

  const load = useCallback(() => {
    instructorAPI.adminApplications('')
      .then(({ data }) => setAll(data.applications || []))
      .catch(() => toast.error(t('Could not load applications')));
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const statusOf = (a) => a.status_label || a.status || 'pending';
  const counts = useMemo(() => Object.fromEntries(STATUSES.map((s) => [s, (all || []).filter((a) => statusOf(a) === s).length])), [all]);
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (all || []).filter((a) => statusOf(a) === tab)
      .filter((a) => !q || `${(a.member || {}).name || ''} ${(a.member || {}).email || ''}`.toLowerCase().includes(q));
  }, [all, tab, search]);

  const resolve = async (app, action) => {
    const verb = action === 'approve' ? t('Approve') : t('Reject');
    if (!window.confirm(`${verb} ${(app.member || {}).name || app.member_id}?`)) return;
    try {
      await instructorAPI.resolveApplication(app.id, action);
      toast.success(action === 'approve' ? t('Approved — instructor role granted.') : t('Rejected'));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Save failed'));
    }
  };

  const emptyTitle = { pending: t('No pending applications'), approved: t('No approved applications'), rejected: t('No rejected applications') }[tab];

  return (
    <div className="flex flex-col gap-[22px]">
      <PageHeader title={t('Instructor applications')} subtitle={t('Members who asked to teach on the platform.')} />

      <FilterPills
        value={tab}
        onChange={setTab}
        items={[
          { key: 'pending', label: t('Pending'), count: all ? counts.pending : undefined },
          { key: 'approved', label: t('Approved'), count: all ? counts.approved : undefined },
          { key: 'rejected', label: t('Rejected'), count: all ? counts.rejected : undefined },
        ]}
      >
        <SearchBox value={search} onChange={setSearch} placeholder={t('Search applicant…')} />
      </FilterPills>

      {!all ? (
        <p className="text-sm text-lms-muted">{t('Loading…')}</p>
      ) : rows.length === 0 ? (
        <EmptyPanel icon={Inbox} title={search ? t('No applicants match your search.') : emptyTitle}>
          {!search && tab === 'pending' && t('Members apply from “Become an instructor” in the LMS footer. Approving one grants the instructor role so they can create their own courses.')}
        </EmptyPanel>
      ) : (
        <div className="flex max-w-[880px] flex-col gap-4">
          {rows.map((a) => {
            const m = a.member || {};
            const contact = [m.email, a.phone || t('no phone'), a.address].filter(Boolean).join(' · ');
            return (
              <article key={a.id} className={`${adminCard} flex flex-col gap-4 px-6 py-[22px]`}>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="flex min-w-0 items-center gap-3.5">
                    <Avatar name={m.name} size={44} />
                    <div className="flex min-w-0 flex-col gap-0.5">
                      <span className="text-base font-bold text-lms-ink">{m.name || '—'}</span>
                      <span className="truncate text-[13px] text-lms-sub">{contact}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2.5">
                    {statusBadge(statusOf(a))}
                    <span className="text-[13px] text-lms-sub">{fmtDate(a.created_at)}</span>
                  </div>
                </div>
                {a.message && (
                  <p className="whitespace-pre-wrap rounded-[10px] bg-lms-raised px-4 py-3.5 text-sm leading-relaxed text-lms-body">{a.message}</p>
                )}
                <div className="flex flex-wrap items-center justify-between gap-3">
                  {a.document_path ? (
                    <a href={a.document_path} target="_blank" rel="noopener noreferrer" className={`${adminBtnOutline} h-10 border-lms-line text-[13.5px] font-semibold`}>
                      <FileText size={15} /> {t('View attached document')}
                    </a>
                  ) : <span className="text-[13px] text-lms-faint">{t('No document attached')}</span>}
                  {statusOf(a) === 'pending' && (
                    <div className="flex gap-2.5">
                      <button type="button" onClick={() => resolve(a, 'reject')} className={adminBtnDanger}>{t('Reject')}</button>
                      <button type="button" onClick={() => resolve(a, 'approve')} className={adminBtnPrimary}>{t('Approve as instructor')}</button>
                    </div>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
